import React, { useEffect, useState } from 'react';

import moment from 'moment';
import Truncate from 'react-truncate';
import { Grow } from '@material-ui/core';
import { AiOutlineSearch } from 'react-icons/ai';

import Input from '../../../../newComponents/Input';

import * as S from './styles';

import DefaultImg from '../../../../assets/no_image.png';

import { formateBrPrice } from '../../../../utils/formateBrPrice';

import { ISegmentGroup } from '../../../../interfaces/ISegmentGroup';
import { IOrderToEdit } from '../../../../services/OrdersService';
import { ICustomer } from '../../../../interfaces/ICustomer';
import { useAuth } from '../../../../hooks/auth';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { ICartItem } from '../../../../interfaces/ICartItem';

interface MainProductsProps {
  order?: IOrderToEdit | null;
  orderId?: any;
  finalPrice: number;
  selectedClient: ICustomer | null;
  setSeletedProductId: (value: React.SetStateAction<number | null>) => void;
  setAsideisClosed: React.Dispatch<React.SetStateAction<boolean>>;
  cart: ICartItem[];
}

const MainProducts: React.FC<MainProductsProps> = (props) => {
  const {
    cart,
    finalPrice,
    order,
    orderId,
    selectedClient,
    setSeletedProductId,
    setAsideisClosed,
  } = props;

  const { addToast } = useToast();
  const { user } = useAuth();

  const [groups, setGroups] = useState<ISegmentGroup[] | null>(null);
  const [orderFilter, setOrderFilter] = useState({
    text: '',
    group: 'all',
  });

  useEffect(() => {
    const loadGroups = async (): Promise<void> => {
      try {
        const { data } = await api.get(`companies/${user.empresa_id}/groups`, {
          params: { include_products: true },
        });
        setGroups(data);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao buscar produtos.',
        });
      }
    };
    loadGroups();
  }, [addToast, user.empresa_id]);

  return (
    <S.Container>
      <div className="header">
        {orderId ? (
          <h1>
            Pedido <span>#{orderId}</span>
          </h1>
        ) : (
          <h1>Novo Pedido</h1>
        )}

        {order && (
          <p>
            Pedido feito por
            <strong>
              {` ${selectedClient?.tradingName ?? 'Venda balcão'}`}
            </strong>
            {', às '}
            <strong>
              {moment(order.createdAt).format('HH:mm')}
              {' de '}
              {moment(order.createdAt).format('DD')}
              {' de '}
              {moment(order.createdAt).format('MMMM')}
              {' de '}
              {moment(order.createdAt).format('YYYY')}
            </strong>
          </p>
        )}

        <div>
          <Input
            icon={AiOutlineSearch}
            placeholder="Digite o nome ou código do produto"
            value={orderFilter.text}
            onChange={(e) => {
              e.persist();
              setOrderFilter({
                group: 'all',
                text: e.target.value,
              });
            }}
          />
        </div>
      </div>

      <S.CustomScrollContainer horizontal hideScrollbars nativeMobileScroll>
        {groups && (
          <S.ContainerItemSelectable
            seleted={orderFilter.group === 'all'}
            onClick={() =>
              setOrderFilter((oldState) => ({
                ...oldState,
                group: 'all',
              }))
            }
          >
            <p>Todos</p>
          </S.ContainerItemSelectable>
        )}
        {groups ? (
          groups?.map((group) => (
            <S.ContainerItemSelectable
              key={group.id}
              seleted={orderFilter.group === group.name}
              onClick={() =>
                setOrderFilter((oldState) => ({
                  ...oldState,
                  group: group.name,
                }))
              }
            >
              <p>{group.name}</p>
            </S.ContainerItemSelectable>
          ))
        ) : (
          <>
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
            <S.CustomSkeletonItemGroup />
          </>
        )}
      </S.CustomScrollContainer>

      <S.ContainerProductItems>
        {groups &&
          groups
            ?.filter(
              (group) =>
                orderFilter.group === 'all' || group.name === orderFilter.group,
            )
            .map((group) =>
              group.products.map((product) => ({
                ...product,
                type: group.type,
              })),
            )
            .flat()
            .filter(
              (product) =>
                product.name
                  .toLowerCase()
                  .includes(orderFilter.text.toLowerCase()) ||
                product.code?.toString().includes(orderFilter.text),
            )
            .map((product) => (
              <div
                key={product.id}
                onClick={() => setSeletedProductId(product.id)}
              >
                <img
                  src={`${process.env.REACT_APP_SERVER_URL}/files/${product.image}`}
                  alt=""
                  onError={({ currentTarget }) => {
                    // eslint-disable-next-line no-param-reassign
                    currentTarget.onerror = null; // prevents looping
                    // eslint-disable-next-line no-param-reassign
                    currentTarget.src = DefaultImg;
                  }}
                />
                <div>
                  <Truncate
                    lines={2}
                    ellipsis={<span>...</span>}
                    trimWhitespace
                  >
                    {product.name}
                  </Truncate>
                  <p>
                    {product.type === 1 &&
                      formateBrPrice(
                        Number(product?.price?.value) *
                          (1 - Number(product?.discount?.value || 0)),
                      )}
                    {(product.type === 2 || product.type === 3) &&
                      `A partir de ${formateBrPrice(
                        product.grids!.reduce((acc: any, cur: any) => {
                          return acc <
                            Number(cur.price.value) *
                              (1 - Number(cur.discount?.value || 0))
                            ? acc
                            : Number(cur.price.value) *
                                (1 - Number(cur.discount?.value || 0));
                        }, Infinity),
                      )}`}
                  </p>
                </div>
              </div>
            ))}
        {groups === null ? (
          <>
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
            <S.CustomSkeletonItemProduct />
          </>
        ) : (
          <></>
        )}
      </S.ContainerProductItems>

      {cart.length > 0 ? (
        <div className="cart" onClick={() => setAsideisClosed(false)}>
          <p>{cart.length}</p>
          <p>VER SACOLA</p>
          <p>{formateBrPrice(finalPrice)}</p>
        </div>
      ) : null}
    </S.Container>
  );
};

export default MainProducts;
